import {useState,useRef,useEffect,useContext,useCallback} from "react"
import ModalContext from "../contexts/ModalContext"
import GlobalContext from "../contexts/GlobalContext"
import useApi from "../hooks/useApi"
import useModal from "../hooks/useModal"
import useToast from "../hooks/useToast"
import ModalHeader from "../components/ModalHeader"
import ModalContent from "../components/ModalContent"
// ModalTitle removed - using custom header content
import ButtonPrimary from "../commons/ButtonPrimary"
import Dropdown from "../commons/Dropdown"
import Datepicker from "../commons/Datepicker"
import Checkbox from "../commons/Checkbox"

export default function CreateLiquidation(){
    // Flag para evitar inicialización múltiple aunque el efecto se vuelva a disparar
    const initRef = useRef(false)
    const [modalContext]=useContext(ModalContext)
    const [globalContext]=useContext(GlobalContext)
    const {manageModal,modalReady}=useModal()
    // useDialog not used in this flow
    const {changeToast}=useToast()
    const requestApi=useApi()
    
    const [currentStep,setCurrentStep]=useState(1)
    const [totalSteps]=useState(4)
    
    // Referencias de campos del paso 1
    const shopsInput=useRef()
    const dateFromInput=useRef()
    const dateToInput=useRef()
    
    // Estados del paso 1
    const [shopsVal,setShopsVal]=useState([])
    const [dateFromVal,setDateFromVal]=useState("")
    const [dateToVal,setDateToVal]=useState("")
    const [paymentDebinVal,setPaymentDebinVal]=useState(true)
    const [paymentQRVal,setPaymentQRVal]=useState(true)
    
    // Opciones para dropdowns
    const [shopsOptions,setShopsOptions]=useState([])
    const [shopSearch,setShopSearch]=useState("")
    
    // Estados multi-cliente
    const [selectedShops,setSelectedShops]=useState([]) // Array de shop IDs seleccionados
    const [currentShopIndex,setCurrentShopIndex]=useState(0) // Índice del shop actual
    const [,setShopLiquidations]=useState({}) // Resultados por shop {shopId: {transactions, summary, etc}}
    // Ref que guarda la lista concreta de shops que se están procesando
    const processingListRef = useRef([])
    // Ref con el índice actual de procesamiento para evitar condiciones de carrera
    const processingIndexRef = useRef(0)
    
    // Estados del paso 2 (Selección de transacciones)
    const [transactions,setTransactions]=useState([])
    const [selectedTransactionIds,setSelectedTransactionIds]=useState([])
    const [loadingTransactions,setLoadingTransactions]=useState(false)
    
    // Estados del paso 3 (Resumen)
    const [previewData,setPreviewData]=useState(null)
    const [loadingPreview,setLoadingPreview]=useState(false)
    
    // Estados del paso 4 (Confirmación)
    const [liquidationResult,setLiquidationResult]=useState(null)
    const [loadingConfirm,setLoadingConfirm]=useState(false)

    // Helper: obtener nombre de shop por id
    const getShopNameById = (id) => {
        const s = shopsOptions.find(o => o.value === id)
        return s ? s.label : `Shop ${id}`
    }
    
    // Helper: convertir dd/MM/yyyy -> yyyy-MM-dd para API
    const toApiDate = (str) => {
        if(!str || typeof str !== 'string') return str
        const parts = str.split('/')
        if(parts.length !== 3) return str
        const [dd, mm, yyyy] = parts
        return `${yyyy}-${mm}-${dd}`
    }
    
    const [excelDownloadUrl, setExcelDownloadUrl] = useState("");

    // Cargar shops habilitados (con emails de liquidación configurados)
    const loadShops=useCallback(()=>{
        return new Promise(function(resolve){
            resolve(
                requestApi("liquidation/shops-enabled",{})
                    .then(data=>{
                        // El backend ya filtra shops con emails configurados
                        const list = (data?.data?.shops) || (data?.data?.items) || (data?.shops) || (data?.items) || []
                        const newOptions = list.map((shop) => ({
                            label: shop.name,
                            value: Number(shop.id)
                        }))
                        return newOptions
                    })
            )
        })
    },[requestApi])

    useEffect(()=>{
        if(initRef.current) return
        initRef.current = true
        modalReady(0)
        Promise.all([loadShops].map(func=>func())).then((result)=>{
            const loadedShops = result[0]
            setShopsOptions(loadedShops)

            // Seleccionar todos los clientes por defecto (guardar solo IDs)
            setShopsVal(loadedShops.map(o => o.value))
            
            // Setear fechas por defecto: últimos 15 días en formato dd/MM/yyyy
            const today = new Date()
            const fifteenDaysAgo = new Date()
            fifteenDaysAgo.setDate(today.getDate() - 15)
            
            const formatDate = (date) => {
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                return `${day}/${month}/${year}`
            }
            
            setDateFromVal(formatDate(fifteenDaysAgo))
            setDateToVal(formatDate(today))
            
            modalReady(1)
        })
    },[loadShops, modalReady])

    const lastAutoSetToRef = useRef(null)
    useEffect(()=>{
        if(!dateFromVal) return
        const parts = dateFromVal.split('/')
        if(parts.length!==3) return
        const day = parseInt(parts[0],10)
        const month = parseInt(parts[1],10)-1
        const year = parseInt(parts[2],10)
        const fromDate = new Date(year,month,day)

        // Fecha hasta = fromDate + 7 días
        const targetTo = new Date(fromDate)
        targetTo.setDate(targetTo.getDate() + 7)

        const dd = String(targetTo.getDate()).padStart(2,'0')
        const mm = String(targetTo.getMonth()+1).padStart(2,'0')
        const yyyy = targetTo.getFullYear()
        const targetToStr = `${dd}/${mm}/${yyyy}`

        if(dateToVal !== targetToStr && lastAutoSetToRef.current !== targetToStr){
            lastAutoSetToRef.current = targetToStr
            setDateToVal(targetToStr)
        }
    },[dateFromVal, dateToVal])

    // Paso 1: Validar y buscar transacciones
    const searchTransactions=()=>{
        const selectedShopsRaw = shopsVal || []
        const selectedShopIds = Array.isArray(selectedShopsRaw)
            ? selectedShopsRaw.map(opt => parseInt((opt && opt.value) ?? opt, 10)).filter(v => !isNaN(v))
            : []

        if(selectedShopIds.length === 0){
            if(shopsInput.current) shopsInput.current.focus()
            changeToast("error","Debe seleccionar al menos un cliente")
            return
        }
        
        if(!dateFromVal || !dateToVal){
            changeToast("error","Debe seleccionar el rango de fechas")
            return
        }
        
        if(!paymentDebinVal && !paymentQRVal){
            changeToast("error","Debe seleccionar al menos un medio de pago")
            return
        }
        
    // Guardar los shops seleccionados
    setSelectedShops(selectedShopIds)
    processingListRef.current = selectedShopIds
    processingIndexRef.current = 0
    setCurrentShopIndex(0)
    loadTransactionsForShop(selectedShopIds[0])
    }
    
    const loadTransactionsForShop=(shopId)=>{
        setLoadingTransactions(true)
        
        let paymentIds = [];
        if (paymentDebinVal) paymentIds.push(53); // Debin
        if (paymentQRVal) paymentIds.push(61);   // QR

        requestApi("liquidation/create-from-transactions",{
            shop_id: parseInt(shopId, 10),
            date_from: toApiDate(dateFromVal),
            date_to: toApiDate(dateToVal),
            payment_ids: paymentIds,
            mode: 'preview' 
        }).then(data=>{
            setLoadingTransactions(false)
            console.log('loadTransactionsForShop - response:', data)
            
            if(data.liquidation_group){
                const group = data.liquidation_group
                console.log('loadTransactionsForShop - found group:', group)
                
                const allTxs = []
                if(group.liquidations && Array.isArray(group.liquidations)){
                    group.liquidations.forEach(liq => {
                        if(liq.transactions && Array.isArray(liq.transactions)){
                            allTxs.push(...liq.transactions)
                        }
                    })
                }
                
                console.log('loadTransactionsForShop - extracted transactions:', allTxs.length)
                
                if(allTxs.length === 0){
                    changeToast("error", `${getShopNameById(parseInt(shopId,10))}: No hay transacciones disponibles con los criterios seleccionados`)
                    continueNextShop()
                    return
                }
                
                setPreviewData(data)
                setTransactions(allTxs)
                setSelectedTransactionIds(allTxs.map(t => t.id))
                console.log('loadTransactionsForShop - going to step 2')
                setCurrentStep(2)
            } else if(data.status === "domain_error") {
                changeToast("error", `${getShopNameById(parseInt(shopId,10))}: ${data.message}`)
                continueNextShop()
            } else {
                console.error('loadTransactionsForShop - unexpected response structure:', data)
                changeToast("error", `${getShopNameById(parseInt(shopId,10))}: Se produjo un error inesperado`)
                continueNextShop()
            }
        }).catch(err=>{
            setLoadingTransactions(false)
            changeToast("error", `${getShopNameById(parseInt(shopId,10))}: Se produjo un error inesperado`)
            continueNextShop()
        })
    }
    
    // Paso 2: Toggle selección de transacción individual
    const toggleTransactionSelection=(txId)=>{
        setSelectedTransactionIds(prev => {
            if(prev.includes(txId)){
                return prev.filter(id => id !== txId)
            } else {
                return [...prev, txId]
            }
        })
    }
    
    // Paso 2: Toggle selección de todas las transacciones
    const toggleAllTransactions=()=>{
        if(selectedTransactionIds.length === transactions.length){
            setSelectedTransactionIds([])
        } else {
            setSelectedTransactionIds(transactions.map(t => t.id))
        }
    }
    
    // Paso 2: Continuar al resumen (ya tenemos el preview, solo recalculamos si hay exclusiones)
    const continueToPreview=()=>{
        if(selectedTransactionIds.length === 0){
            changeToast("error","Debe seleccionar al menos una transacción")
            return
        }
        
        const excludedIds = transactions
            .filter(t => !selectedTransactionIds.includes(t.id))
            .map(t => t.id)
        
        console.log('continueToPreview - excludedIds:', excludedIds.length, 'previewData:', previewData)
        
        if(excludedIds.length > 0){
            // Hay exclusiones, recalcular preview
            const currentShop = selectedShops[currentShopIndex]
            setLoadingPreview(true)
            

            let paymentIds = [];
            if (paymentDebinVal) paymentIds.push(53); // Debin
            if (paymentQRVal) paymentIds.push(61);   // QR
            
            requestApi("liquidation/create-from-transactions",{
                shop_id: parseInt(currentShop, 10),
                date_from: toApiDate(dateFromVal),
                date_to: toApiDate(dateToVal),
                payment_ids: paymentIds,
                excluded_transaction_ids: excludedIds,
                mode: 'preview' // Previsualización sin guardar
            }).then(data=>{
                setLoadingPreview(false)
                console.log('continueToPreview - recalculated preview data:', data)
                if(data.liquidation_group){
                    setPreviewData(data)
                    setCurrentStep(3)
                } else if(data.status === "domain_error") {
                    changeToast("error", `${getShopNameById(parseInt(currentShop,10))}: ${data.message}`)
                    continueNextShop()
                } else {
                    changeToast("error", `${getShopNameById(parseInt(currentShop,10))}: Se produjo un error inesperado`)
                    continueNextShop()
                }
            }).catch(err=>{
                setLoadingPreview(false)
                changeToast("error", `${getShopNameById(parseInt(currentShop,10))}: Se produjo un error inesperado`)
                continueNextShop()
            })
        } else {
            console.log('continueToPreview - using existing preview, going to step 3')
            setCurrentStep(3)
        }
    }
    
    // Paso 3: Confirmar y guardar liquidación
    const confirmAndGenerate=()=>{
        console.log('confirmAndGenerate - starting with shop:', selectedShops[currentShopIndex])
        const currentShop = selectedShops[currentShopIndex]
        setLoadingConfirm(true)
        setExcelDownloadUrl("");
        let paymentIds = [];
        if (paymentDebinVal) paymentIds.push(53); // Debin
        if (paymentQRVal) paymentIds.push(61);   // QR
        
        const excludedIds = transactions
            .filter(t => !selectedTransactionIds.includes(t.id))
            .map(t => t.id)
        
        console.log('confirmAndGenerate - excludedIds:', excludedIds.length, 'paymentIds:', paymentIds)
        
        requestApi("liquidation/create-from-transactions",{
            shop_id: parseInt(currentShop, 10),
            date_from: toApiDate(dateFromVal),
            date_to: toApiDate(dateToVal),
            payment_ids: paymentIds,
            excluded_transaction_ids: excludedIds.length > 0 ? excludedIds : undefined,
            user_id: globalContext.user_id || null,
            mode: 'save' // Save = guardar liquidación
        }).then(data=>{
            setLoadingConfirm(false)
            console.log('confirmAndGenerate - response:', data)
            
            if(data.liquidation_group){
                setShopLiquidations(prev => ({
                    ...prev,
                    [currentShop]: data
                }))
                setLiquidationResult(data)
                
                // Generar  Excel 
                const groupId = data.liquidation_group.id
                console.log('Generating Excel for liquidation group:', groupId)
                
                requestApi("liquidation/generate-sheet", {
                    liquidation_group_id: groupId
                }).then(excelData => {
                    console.log('Excel generation response:', excelData)
                    if (excelData.status === "success" && excelData.download_path) {
                        const fullUrl = globalContext.base_url + excelData.download_path.replace(/^\//, "")
                        setExcelDownloadUrl(fullUrl)
                        console.log('Excel download URL set:', fullUrl)
                    } else {
                        changeToast("warning", `${getShopNameById(parseInt(currentShop,10))}: Liquidación creada pero no se pudo generar el Excel`)
                    }
                    console.log('confirmAndGenerate - going to step 4')
                    setCurrentStep(4)
                }).catch(err => {
                    console.error('Error generating Excel:', err)
                    changeToast("warning", `${getShopNameById(parseInt(currentShop,10))}: Liquidación creada pero no se pudo generar el Excel`)
                    setCurrentStep(4)
                })
            } else if(data.status === "domain_error") {
                changeToast("error", `${getShopNameById(parseInt(currentShop,10))}: ${data.message}`)
                continueNextShop()
            } else {
                changeToast("error", `${getShopNameById(parseInt(currentShop,10))}: Se produjo un error inesperado`)
                continueNextShop()
            }
        }).catch(err=>{
            setLoadingConfirm(false)
            changeToast("error", `${getShopNameById(parseInt(currentShop,10))}: Se produjo un error inesperado`)
            continueNextShop()
        })
    }

    // Paso 4: Descargar Excel
    const handleDownloadExcel = () => {
        if (!excelDownloadUrl) {
            changeToast("error", "No hay URL de descarga disponible");
            return;
        }
        window.open(excelDownloadUrl, '_blank');
    }
    
    // Paso 4: Continuar con el siguiente cliente o finalizar
    const continueNextShop=()=>{
        const list = Array.isArray(processingListRef.current) ? processingListRef.current : selectedShops
        const nextIndex = processingIndexRef.current + 1

        if(nextIndex < list.length){
            processingIndexRef.current = nextIndex
            setCurrentShopIndex(nextIndex)
            setTransactions([])
            setSelectedTransactionIds([])
            setPreviewData(null)
            setLiquidationResult(null)
            setExcelDownloadUrl("")
            setCurrentStep(1) // Volver al paso 1 para mostrar loading mientras carga
            loadTransactionsForShop(list[nextIndex])
        } else {
            changeToast("success","Todas las liquidaciones fueron creadas correctamente")
            manageModal("hide")
            if(typeof modalContext?.reloadCallback === 'function'){
                modalContext.reloadCallback()
            }
        }
    }
    
    // Volver al paso anterior
    const goBackStep=()=>{
        if(currentStep > 1){
            setCurrentStep(currentStep - 1)
        }
    }

    // Obtener nombre del shop actual
    const getCurrentShopName=()=>{
        if(selectedShops.length === 0 || currentShopIndex >= selectedShops.length) return ""
        const shopId = selectedShops[currentShopIndex]
        const shop = shopsOptions.find(s => s.value === shopId)
        return shop ? shop.label : ""
    }
    
    // Renderizar título del header según paso actual
    const renderHeaderTitle=()=>{
        const shopName = getCurrentShopName()
        const shopProgress = selectedShops.length > 1 ? ` (${currentShopIndex + 1}/${selectedShops.length})` : ""
        
        switch(currentStep){
            case 1:
                return "Parámetros de la liquidación"
            case 2:
                return `Selección de Transacciones - ${shopName}${shopProgress}`
            case 3:
                return `Resumen de Liquidación - ${shopName}${shopProgress}`
            case 4:
                return `Confirmación - ${shopName}${shopProgress}`
            default:
                return "Liquidación"
        }
    }

    return (
        <>
            <ModalHeader>
                <div style={{display:"flex",flexDirection:"column",width:"100%"}}>
                    <div style={{fontSize:18,fontWeight:600,color:"#624DE3",textAlign:"center"}}>
                        Paso {currentStep} de {totalSteps}: {renderHeaderTitle()}
                    </div>
                    <div style={{height:12}}></div>
                </div>
            </ModalHeader>
            <ModalContent>
                
                {/* PASO 1: Parámetros */}
                {currentStep === 1 && (

                    <div className="modal-form">
                        {/* Selección de clientes: búsqueda y botones en una sola línea */}
                        <div style={{display:"flex", gap:"8px", alignItems:"center", marginBottom:"8px"}}>
                            <input
                                ref={shopsInput}
                                type="text"
                                value={shopSearch}
                                onChange={(e)=>setShopSearch(e.target.value)}
                                placeholder="Buscar cliente por nombre..."
                                style={{flex:1, padding:"10px 12px", border:"1px solid #E0E0E0", borderRadius:"8px", fontSize:"14px"}}
                            />
                            <button
                                type="button"
                                onClick={()=>setShopsVal(shopsOptions.map(s=>s.value))}
                                style={{padding:"10px 12px", border:"1px solid #624DE3", color:"#624DE3", background:"#FFFFFF", borderRadius:"8px", fontWeight:600, cursor:"pointer"}}
                            >
                                Todos
                            </button>
                            <button
                                type="button"
                                onClick={()=>setShopsVal([])}
                                style={{padding:"10px 12px", border:"1px solid #624DE3", color:"#624DE3", background:"#FFFFFF", borderRadius:"8px", fontWeight:600, cursor:"pointer"}}
                            >
                                Ninguno
                            </button>
                        </div>
                        <div style={{fontSize:"14px",color:"#624DE3",fontWeight:500, marginBottom:"8px"}}>
                            {(shopsVal||[]).length} seleccionados de {shopsOptions.length}
                        </div>

                                                <div style={{maxHeight:"260px", overflowY:"auto", border:"1px solid #E0E0E0", borderRadius:"8px"}}>
                                                    {shopsOptions
                                                        .filter(s => (s.label||"").toLowerCase().includes(shopSearch.toLowerCase()))
                                                        .map((s, index, arr)=>{
                                                            const checked = (shopsVal||[]).includes(s.value)
                                                            return (
                                                                <div
                                                                    key={s.value}
                                                                    onClick={()=>{
                                                                        setShopsVal(prev => {
                                                                            const list = prev||[]
                                                                            return list.includes(s.value)
                                                                                ? list.filter(id => id !== s.value)
                                                                                : [...list, s.value]
                                                                        })
                                                                    }}
                                                                    style={{
                                                                        display:"flex",
                                                                        alignItems:"center",
                                                                        padding:"10px 12px",
                                                                        borderBottom: index < arr.length - 1 ? "1px solid #E0E0E0" : "none",
                                                                        cursor:"pointer",
                                                                        background: checked ? "#F0EEFF" : "#FFFFFF"
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={checked}
                                                                        onChange={()=>{}}
                                                                        style={{marginRight:"12px", width:"18px", height:"18px", cursor:"pointer"}}
                                                                    />
                                                                    <div style={{flex:1, fontSize:"14px", color:"#32177A", fontWeight:500}}>
                                                                        {s.label}
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                </div>
                        
                        <div className="modal-form-divisor"></div>
                        
                        {/* Rango de fechas */}
                        <div className="modal-explanation">
                            Rango de fechas de las transacciones
                        </div>
                        <div className="modal-form-columns">
                            <div>
                                <div style={{marginBottom:"8px",fontSize:"13px",color:"#624DE3",fontWeight:"500"}}>
                                    Desde
                                </div>
                                <Datepicker 
                                    ref={dateFromInput}
                                    val={dateFromVal}
                                    setVal={setDateFromVal}
                                />
                            </div>
                            <div>
                                <div style={{marginBottom:"8px",fontSize:"13px",color:"#624DE3",fontWeight:"500"}}>
                                    Hasta
                                </div>
                                <Datepicker 
                                    ref={dateToInput}
                                    val={dateToVal}
                                    setVal={setDateToVal}
                                />
                            </div>
                        </div>
                        
                        <div className="modal-form-divisor"></div>
                        
                        {/* Medios de pago */}
                        <div className="modal-explanation">
                            Medios de pago a incluir
                        </div>
                        <div style={{display:"flex",gap:"16px",flexDirection:"column"}}>
                            <Checkbox 
                                defaultValue={paymentDebinVal}
                                onChange={(value)=>setPaymentDebinVal(value)}
                                label="Debin"
                            />
                            <Checkbox 
                                defaultValue={paymentQRVal}
                                onChange={(value)=>setPaymentQRVal(value)}
                                label="QR"
                            />
                        </div>
                        
                        <div className="modal-form-divisor"></div>
                        <div className="modal-form-divisor"></div>
                        
                        {/* Botones de acción */}
                        <div className="modal-commands">
                            <ButtonPrimary 
                                label={loadingTransactions ? "Buscando..." : "Buscar Transacciones"}
                                clickEvent={searchTransactions}
                                disabled={loadingTransactions}
                            />
                        </div>
                    </div>
                )}
                
                {/* PASO 2: Selección de transacciones */}
                {currentStep === 2 && (
                    <div className="modal-form">
                        <div className="modal-explanation">
                            Seleccione las transacciones a incluir en la liquidación
                        </div>
                        
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:"#F0EEFF",borderRadius:"8px",marginBottom:"16px"}}>
                            <div style={{fontSize:"14px",color:"#624DE3",fontWeight:"500"}}>
                                {selectedTransactionIds.length} de {transactions.length} transacciones seleccionadas
                            </div>
                            <div 
                                onClick={toggleAllTransactions}
                                style={{fontSize:"13px",color:"#624DE3",cursor:"pointer",textDecoration:"underline"}}
                            >
                                {selectedTransactionIds.length === transactions.length ? "Deseleccionar todas" : "Seleccionar todas"}
                            </div>
                        </div>
                        
                        <div style={{maxHeight:"400px",overflowY:"auto",border:"1px solid #E0E0E0",borderRadius:"8px"}}>
                            {transactions.map((tx,index)=>(
                                <div 
                                    key={tx.id}
                                    onClick={()=>toggleTransactionSelection(tx.id)}
                                    style={{
                                        display:"flex",
                                        alignItems:"center",
                                        padding:"12px 16px",
                                        borderBottom: index < transactions.length - 1 ? "1px solid #E0E0E0" : "none",
                                        cursor:"pointer",
                                        background: selectedTransactionIds.includes(tx.id) ? "#F0EEFF" : "#FFFFFF",
                                        transition:"background 0.2s"
                                    }}
                                >
                                    <input 
                                        type="checkbox" 
                                        checked={selectedTransactionIds.includes(tx.id)}
                                        onChange={()=>{}}
                                        style={{marginRight:"12px",cursor:"pointer",width:"18px",height:"18px"}}
                                    />
                                    <div style={{flex:1}}>
                                        <div style={{fontSize:"14px",color:"#32177A",fontWeight:"500",marginBottom:"4px"}}>
                                            {tx.nombre_pagador || "Sin nombre"} - ${tx.monto || "0,00"}
                                        </div>
                                        <div style={{fontSize:"12px",color:"#666"}}>
                                            {tx.medio_de_pago} • {tx.fecha} • ID: {tx.id}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        <div className="modal-form-divisor"></div>
                        <div className="modal-form-divisor"></div>
                        
                        <div className="modal-commands" style={{display:"flex",gap:"12px"}}>
                            <button 
                                onClick={goBackStep}
                                style={{
                                    flex:1,
                                    padding:"12px 24px",
                                    background:"#FFFFFF",
                                    border:"1px solid #624DE3",
                                    color:"#624DE3",
                                    borderRadius:"8px",
                                    fontSize:"14px",
                                    fontWeight:"600",
                                    cursor:"pointer"
                                }}
                            >
                                Volver
                            </button>
                            <ButtonPrimary 
                                label={loadingPreview ? "Generando resumen..." : "Continuar al Resumen"}
                                clickEvent={continueToPreview}
                                disabled={loadingPreview || selectedTransactionIds.length === 0}
                                style={{flex:2}}
                            />
                        </div>
                    </div>
                )}
                
                {/* PASO 3: Resumen */}
                {currentStep === 3 && previewData && previewData.liquidation_group && (
                    <div className="modal-form">
                        <div className="modal-explanation">
                            Revise el resumen antes de confirmar
                        </div>
                        
                        <div style={{background:"#F0EEFF",borderRadius:"8px",padding:"20px",marginBottom:"16px"}}>
                            <div style={{fontSize:"16px",color:"#32177A",fontWeight:"600",marginBottom:"16px"}}>
                                Resumen de Liquidaciones
                            </div>
                            
                            {previewData.liquidation_group.liquidations && previewData.liquidation_group.liquidations.map((liq,index)=>(
                                <div key={index} style={{marginBottom:"16px",paddingBottom:"16px",borderBottom: index < previewData.liquidation_group.liquidations.length - 1 ? "1px solid #624DE3" : "none"}}>
                                    <div style={{fontSize:"14px",color:"#624DE3",fontWeight:"600",marginBottom:"8px"}}>
                                        {liq.payment_name}
                                    </div>
                                    <div style={{display:"flex",justifyContent:"space-between",fontSize:"13px",color:"#666",marginBottom:"4px"}}>
                                        <span>Transacciones:</span>
                                        <span>{liq.transactions_count}</span>
                                    </div>
                                    <div style={{display:"flex",justifyContent:"space-between",fontSize:"13px",color:"#666",marginBottom:"4px"}}>
                                        <span>Monto bruto:</span>
                                        <span>${parseFloat(liq.amount || 0).toLocaleString('es-AR',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                                    </div>
                                    
                                    {/* Mostrar cada descuento por separado */}
                                    {liq.discounts && liq.discounts.length > 0 && liq.discounts.map((discount, dIdx) => (
                                        <div key={dIdx} style={{display:"flex",justifyContent:"space-between",fontSize:"13px",color:"#666",marginBottom:"4px"}}>
                                            <span>{discount.description}:</span>
                                            <span>-${parseFloat(discount.amount || 0).toLocaleString('es-AR',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                                        </div>
                                    ))}
                                    
                                    <div style={{display:"flex",justifyContent:"space-between",fontSize:"14px",color:"#32177A",fontWeight:"600",marginTop:"8px"}}>
                                        <span>Monto neto:</span>
                                        <span>${parseFloat(liq.net_amount || 0).toLocaleString('es-AR',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                                    </div>
                                </div>
                            ))}
                            
                            <div style={{borderTop:"2px solid #624DE3",marginTop:"16px",paddingTop:"16px"}}>
                                <div style={{display:"flex",justifyContent:"space-between",fontSize:"14px",color:"#666",marginBottom:"8px"}}>
                                    <span>Total Transacciones:</span>
                                    <span>{previewData.liquidation_group.summary?.total_transactions || 0}</span>
                                </div>
                                <div style={{display:"flex",justifyContent:"space-between",fontSize:"14px",color:"#666",marginBottom:"8px"}}>
                                    <span>Monto Total Bruto:</span>
                                    <span>${parseFloat(previewData.liquidation_group.summary?.total_amount || 0).toLocaleString('es-AR',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                                </div>
                                <div style={{display:"flex",justifyContent:"space-between",fontSize:"14px",color:"#666",marginBottom:"8px"}}>
                                    <span>Descuento Total:</span>
                                    <span>-${parseFloat(previewData.liquidation_group.summary?.total_discount || 0).toLocaleString('es-AR',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                                </div>
                                <div style={{display:"flex",justifyContent:"space-between",fontSize:"16px",color:"#32177A",fontWeight:"700",marginTop:"12px"}}>
                                    <span>Total Neto a Liquidar:</span>
                                    <span>${parseFloat(previewData.liquidation_group.summary?.total_net_amount || 0).toLocaleString('es-AR',{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div style={{background:"#FFFFFF",border:"1px solid #E0E0E0",borderRadius:"8px",padding:"16px"}}>
                            <div style={{fontSize:"14px",color:"#666",marginBottom:"8px"}}>
                                <strong>Cliente:</strong> {getCurrentShopName()}
                            </div>
                            <div style={{fontSize:"14px",color:"#666"}}>
                                <strong>Período:</strong> {dateFromVal} - {dateToVal}
                            </div>
                        </div>
                        
                        <div className="modal-form-divisor"></div>
                        <div className="modal-form-divisor"></div>
                        
                        <div className="modal-commands" style={{display:"flex",gap:"12px"}}>
                            <button 
                                onClick={goBackStep}
                                style={{
                                    flex:1,
                                    padding:"12px 24px",
                                    background:"#FFFFFF",
                                    border:"1px solid #624DE3",
                                    color:"#624DE3",
                                    borderRadius:"8px",
                                    fontSize:"14px",
                                    fontWeight:"600",
                                    cursor:"pointer"
                                }}
                            >
                                Volver
                            </button>
                            <ButtonPrimary 
                                label={loadingConfirm ? "Generando..." : "Confirmar y Generar"}
                                clickEvent={confirmAndGenerate}
                                disabled={loadingConfirm}
                                style={{flex:2}}
                            />
                        </div>
                    </div>
                )}
                
                {/* PASO 4: Confirmación */}
                {currentStep === 4 && liquidationResult && (
                    <div className="modal-form">
                        <div style={{textAlign:"center",padding:"20px"}}>
                            <div style={{fontSize:"48px",marginBottom:"16px"}}>✅</div>
                            <div style={{fontSize:"18px",color:"#32177A",fontWeight:"600",marginBottom:"8px"}}>
                                Liquidación generada correctamente
                            </div>
                            <div style={{fontSize:"14px",color:"#666",marginBottom:"24px"}}>
                                Se ha enviado el email automático al cliente
                            </div>
                        </div>
                        
                        <div style={{background:"#F0EEFF",borderRadius:"8px",padding:"20px",marginBottom:"16px"}}>
                            <div style={{fontSize:"14px",color:"#624DE3",marginBottom:"12px"}}>
                                <strong>ID de Liquidación:</strong> {liquidationResult.liquidation_group?.id || liquidationResult.id}
                            </div>
                            <div style={{fontSize:"14px",color:"#624DE3",marginBottom:"12px"}}>
                                <strong>Cliente:</strong> {getCurrentShopName()}
                            </div>
                            <div style={{fontSize:"14px",color:"#624DE3"}}>
                                <strong>Monto Neto a Transferir:</strong> ${parseFloat(liquidationResult.liquidation_group?.summary?.total_net_amount || liquidationResult.net_amount || 0).toLocaleString('es-AR',{minimumFractionDigits:2,maximumFractionDigits:2})}
                            </div>
                        </div>
                        
                        <div style={{marginBottom:"16px"}}>
                            <ButtonPrimary
                                label="Descargar Excel"
                                clickEvent={handleDownloadExcel}
                                disabled={!excelDownloadUrl}
                            />
                        </div>
                        
                        <div className="modal-form-divisor"></div>
                        <div className="modal-form-divisor"></div>
                        
                        <div className="modal-commands">
                            {currentShopIndex < selectedShops.length - 1 ? (
                                <ButtonPrimary 
                                    label={`Continuar con ${shopsOptions.find(s => s.value === selectedShops[currentShopIndex + 1])?.label || "siguiente cliente"}`}
                                    clickEvent={continueNextShop}
                                />
                            ) : (
                                <ButtonPrimary 
                                    label="Finalizar"
                                    clickEvent={continueNextShop}
                                />
                            )}
                        </div>
                    </div>
                )}
            </ModalContent>
        </>
    )
}
